/**
 * Drydock campaign — a planned shipyard period during which a vessel
 * is taken out of service for hull/machinery work, certificate
 * renewals, and grouped work orders. Mirrors the backend Phase 5b
 * Prisma model `DrydockCampaign`.
 *
 * Lifecycle: planned → in_progress → completed.
 * cancelled is reachable from any non-cancelled state and is terminal.
 *
 * Two collections hang off a campaign on the wire:
 *   - work orders linked via `WorkOrder.drydockCampaignId`
 *   - certificates renewed via `Certificate.renewedDuringDrydockCampaignId`
 * Both are exposed by dedicated GET sub-resources rather than embedded
 * in the campaign payload.
 */
export interface DrydockCampaign {
  id: string;
  companyId: string;
  vesselId: string;

  title: string;
  description: string | null;

  startDate: string; // ISO date-time
  endDate: string | null;

  location: string | null;
  shipyard: string | null;

  /** Planned-only budget — actual spend is not tracked in 5b. */
  totalBudgetAmount: string | null; // Decimal(15,2) → string on the wire
  totalBudgetCurrency: string | null; // ISO 4217

  status: DrydockCampaignStatus;

  /** Set when status transitions to `completed`. */
  completedAt: string | null;

  /** Cancellation metadata — populated only when status='cancelled'. */
  cancelledAt: string | null;
  cancelReason: string | null;

  notes: string | null;

  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Lifecycle states. Backend transitions are guarded by service-level
 * checks: start requires `planned`, complete requires `in_progress`,
 * cancel forbids `cancelled` only.
 */
export const DRYDOCK_CAMPAIGN_STATUSES = [
  'planned',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type DrydockCampaignStatus = (typeof DRYDOCK_CAMPAIGN_STATUSES)[number];

/**
 * Whether the campaign can still receive new work orders. The form
 * picker uses this to grey out completed/cancelled campaigns when
 * linking a WO.
 */
export function isOpenForLinking(c: DrydockCampaign): boolean {
  return c.status === 'planned' || c.status === 'in_progress';
}

/**
 * Status colour mapping, mirroring the badge convention used by the
 * other domain pages (work orders, certificates, maintenance plans):
 *   planned      → muted (greyed, not yet started)
 *   in_progress  → amber (active, attention required)
 *   completed    → emerald (success terminal)
 *   cancelled    → red (failure terminal)
 */
export function statusVisualTone(
  status: DrydockCampaignStatus,
): 'emerald' | 'amber' | 'red' | 'muted' {
  switch (status) {
    case 'planned':
      return 'muted';
    case 'in_progress':
      return 'amber';
    case 'completed':
      return 'emerald';
    case 'cancelled':
      return 'red';
  }
}
